import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Link2, Send } from 'lucide-react';
import { useLocale, useT } from '../lib/i18n.js';
import { errorMessage } from '../lib/properties.js';
import {
  cancelViewingBooking,
  createViewingLink,
  createViewingSlots,
  listViewingBookings,
  listViewingSlots,
  previewViewingInvitation,
  sendViewingInvitation,
  withdrawViewingSlot,
  type CreatedViewingLink,
  type MailPreview,
} from '../lib/viewings-api.js';
import { formatViewingRange } from '../lib/viewing-format.js';
import { V4Group, V4Pill, V4Row } from './ux-v4.js';
import { DateField, TimeField } from './date-field.js';

/**
 * Karta nemovitosti → „Prohlídky" (krok CRM4, bod 1).
 *
 * Tři věci pod sebou, každá s jedním tlačítkem (DESIGN-UX-V4 pravidlo 2):
 *  1. volné termíny — přidat termín; kolize s kalendářem makléře vrátí
 *     server jako „nezaloženo" a termín, který koliduje až dodatečně, je
 *     označený (zájemci se nenabídne),
 *  2. odkaz pro zájemce — založit → NÁHLED e-mailu → teprve „Odeslat"
 *     (nic neodejde bez potvrzení makléře),
 *  3. rezervace — kdo a kdy; zrušit jde po potvrzení (zájemce dostane e-mail).
 */

const DURATIONS = [30, 45, 60] as const;

function toIso(date: string, time: string): string | null {
  if (date === '' || time === '') return null;
  // Datum a čas zadává makléř v místním čase prohlížeče (makléř v ČR =
  // Europe/Prague, ve kterém je i ukazujeme — `viewing-format.ts`).
  const local = new Date(`${date}T${time}:00`);
  return Number.isNaN(local.getTime()) ? null : local.toISOString();
}

export function PropertyViewingsPanel({ propertyId }: { readonly propertyId: string }): React.JSX.Element {
  const t = useT();
  const locale = useLocale();
  const queryClient = useQueryClient();
  const slots = useQuery({ queryKey: ['viewing-slots', propertyId], queryFn: () => listViewingSlots(propertyId) });
  const bookings = useQuery({
    queryKey: ['viewing-bookings', 'property', propertyId],
    queryFn: () => listViewingBookings({ propertyId, status: 'booked' }),
  });

  const [date, setDate] = React.useState('');
  const [time, setTime] = React.useState('');
  const [duration, setDuration] = React.useState<(typeof DURATIONS)[number]>(30);
  const [slotNote, setSlotNote] = React.useState<string | null>(null);
  const [contactName, setContactName] = React.useState('');
  const [contactEmail, setContactEmail] = React.useState('');
  const [link, setLink] = React.useState<CreatedViewingLink | null>(null);
  const [preview, setPreview] = React.useState<MailPreview | null>(null);
  const [linkNote, setLinkNote] = React.useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  async function refresh(): Promise<void> {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['viewing-slots', propertyId] }),
      queryClient.invalidateQueries({ queryKey: ['viewing-bookings'] }),
    ]);
  }

  async function run(action: () => Promise<void>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const startsAt = toIso(date, time);

  return (
    <div className="rh-crm4-panel" data-testid="property-viewings">
      {error !== null ? (
        <p role="alert" className="rh-a2-error" data-testid="property-viewings-error">
          {error}
        </p>
      ) : null}

      {/* ------------------------------------------------ volné termíny */}
      <h3 className="rh-v4-h3">{t('webAdmin.viewings.slotsHeading')}</h3>
      <V4Group testId="property-viewings-slots" label={t('webAdmin.viewings.slotsHeading')}>
        {(slots.data ?? []).length === 0 ? (
          <V4Row title={t('webAdmin.viewings.noSlots')} testId="property-viewings-no-slots" />
        ) : (
          (slots.data ?? []).map((slot) => (
            <V4Row
              key={slot.id}
              testId={`property-viewings-slot-${slot.id}`}
              icon={
                <span className="rh-v4-ico" aria-hidden="true">
                  <CalendarClock size={16} strokeWidth={2} />
                </span>
              }
              title={formatViewingRange(slot.startsAt, slot.endsAt, locale)}
              sub={
                slot.bookedBy !== null
                  ? t('webAdmin.viewings.slotBooked', { name: slot.bookedBy })
                  : slot.status === 'withdrawn'
                    ? t('webAdmin.viewings.slotWithdrawn')
                    : slot.collides
                      ? t('webAdmin.viewings.slotCollides')
                      : t('webAdmin.viewings.slotFree')
              }
              right={
                slot.bookingId === null && slot.status === 'open' ? (
                  slot.collides ? (
                    <V4Pill tone="warn">{t('webAdmin.viewings.collisionPill')}</V4Pill>
                  ) : (
                    <button
                      type="button"
                      className="rh-v4-btn rh-v4-btn--plain"
                      disabled={busy}
                      onClick={() => void run(async () => { await withdrawViewingSlot(slot.id); await refresh(); })}
                      data-testid={`property-viewings-withdraw-${slot.id}`}
                    >
                      {t('webAdmin.viewings.withdraw')}
                    </button>
                  )
                ) : undefined
              }
            />
          ))
        )}
      </V4Group>
      <V4Group testId="property-viewings-add" label={t('webAdmin.viewings.addHeading')}>
        <div className="rh-v4-sect__body rh-crm4-form">
          <label className="rh-v4-field">
            <span className="rh-v4-field__label">{t('webAdmin.viewings.date')}</span>
            <DateField value={date} onValueChange={setDate} testId="property-viewings-date" />
          </label>
          <label className="rh-v4-field">
            <span className="rh-v4-field__label">{t('webAdmin.viewings.time')}</span>
            <TimeField value={time} onValueChange={setTime} testId="property-viewings-time" />
          </label>
          <label className="rh-v4-field">
            <span className="rh-v4-field__label">{t('webAdmin.viewings.duration')}</span>
            <select
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value) as (typeof DURATIONS)[number])}
              data-testid="property-viewings-duration"
            >
              {DURATIONS.map((m) => (
                <option key={m} value={m}>
                  {t('webAdmin.viewings.minutes', { minutes: m })}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="rh-v4-btn rh-v4-btn--tinted"
            disabled={busy || startsAt === null}
            onClick={() =>
              void run(async () => {
                if (startsAt === null) return;
                const endsAt = new Date(new Date(startsAt).getTime() + duration * 60_000).toISOString();
                const result = await createViewingSlots(propertyId, [{ startsAt, endsAt }]);
                const skipped = result.skipped[0];
                setSlotNote(
                  skipped === undefined
                    ? null
                    : skipped.reason === 'slot_collision'
                      ? t('webAdmin.viewings.skippedCollision')
                      : t('webAdmin.viewings.skippedPast'),
                );
                await refresh();
              })
            }
            data-testid="property-viewings-add-slot"
          >
            {t('webAdmin.viewings.addSlot')}
          </button>
          {slotNote !== null ? (
            <p role="status" className="rh-a2-error" data-testid="property-viewings-slot-note">
              {slotNote}
            </p>
          ) : null}
        </div>
      </V4Group>

      {/* ------------------------------------------- odkaz pro zájemce */}
      <h3 className="rh-v4-h3">{t('webAdmin.viewings.linkHeading')}</h3>
      <V4Group testId="property-viewings-link" label={t('webAdmin.viewings.linkHeading')}>
        <div className="rh-v4-sect__body rh-crm4-form">
          {link === null ? (
            <>
              <label className="rh-v4-field">
                <span className="rh-v4-field__label">{t('webAdmin.viewings.contactName')}</span>
                <input value={contactName} onChange={(e) => setContactName(e.target.value)} data-testid="property-viewings-contact-name" />
              </label>
              <label className="rh-v4-field">
                <span className="rh-v4-field__label">{t('webAdmin.viewings.contactEmail')}</span>
                <input
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  data-testid="property-viewings-contact-email"
                />
              </label>
              <button
                type="button"
                className="rh-v4-btn rh-v4-btn--tinted"
                disabled={busy || contactEmail.trim() === ''}
                onClick={() =>
                  void run(async () => {
                    const created = await createViewingLink({
                      propertyId,
                      ...(contactName.trim() !== '' ? { contactName: contactName.trim() } : {}),
                      contactEmail: contactEmail.trim(),
                    });
                    setLink(created);
                    setPreview(await previewViewingInvitation(created.link.id, created.token));
                    setLinkNote(null);
                  })
                }
                data-testid="property-viewings-create-link"
              >
                <Link2 size={16} strokeWidth={2} aria-hidden="true" /> {t('webAdmin.viewings.createLink')}
              </button>
            </>
          ) : (
            <>
              <p className="rh-crm4-url" data-testid="property-viewings-url">
                {link.url}
              </p>
              {preview !== null ? (
                <div className="rh-crm4-preview" data-testid="property-viewings-preview">
                  <div className="rh-crm4-preview__meta">
                    {t('webAdmin.viewings.previewTo', { to: preview.to ?? '—' })}
                  </div>
                  <div className="rh-crm4-preview__subject">{preview.subject}</div>
                  <pre className="rh-crm4-preview__text">{preview.text}</pre>
                </div>
              ) : null}
              <div className="rh-crm4-actions">
                <button
                  type="button"
                  className="rh-v4-btn rh-v4-btn--filled"
                  disabled={busy || preview === null}
                  onClick={() =>
                    void run(async () => {
                      await sendViewingInvitation(link.link.id, link.token);
                      setLinkNote(t('webAdmin.viewings.invitationQueued'));
                      setLink(null);
                      setPreview(null);
                      setContactName('');
                      setContactEmail('');
                    })
                  }
                  data-testid="property-viewings-send"
                >
                  <Send size={16} strokeWidth={2} aria-hidden="true" /> {t('webAdmin.viewings.sendInvitation')}
                </button>
                <button
                  type="button"
                  className="rh-v4-btn rh-v4-btn--plain"
                  onClick={() => {
                    setLink(null);
                    setPreview(null);
                  }}
                  data-testid="property-viewings-dont-send"
                >
                  {t('webAdmin.viewings.dontSend')}
                </button>
              </div>
            </>
          )}
          {linkNote !== null ? (
            <p role="status" className="rh-a2-status" data-testid="property-viewings-link-note">
              {linkNote}
            </p>
          ) : null}
        </div>
      </V4Group>

      {/* ------------------------------------------------- rezervace */}
      <h3 className="rh-v4-h3">{t('webAdmin.viewings.bookingsHeading')}</h3>
      <V4Group testId="property-viewings-bookings" label={t('webAdmin.viewings.bookingsHeading')}>
        {(bookings.data ?? []).length === 0 ? (
          <V4Row title={t('webAdmin.viewings.noBookings')} testId="property-viewings-no-bookings" />
        ) : (
          (bookings.data ?? []).map((b) => (
            <V4Row
              key={b.id}
              testId={`property-viewings-booking-${b.id}`}
              title={b.contactName}
              sub={`${formatViewingRange(b.startsAt, b.endsAt, locale)} · ${b.contactEmail}`}
              right={
                confirmCancel === b.id ? (
                  <span className="rh-crm4-actions">
                    <button
                      type="button"
                      className="rh-v4-btn rh-v4-btn--tinted"
                      disabled={busy}
                      onClick={() =>
                        void run(async () => {
                          await cancelViewingBooking(b.id);
                          setConfirmCancel(null);
                          await refresh();
                        })
                      }
                      data-testid={`property-viewings-cancel-confirm-${b.id}`}
                    >
                      {t('webAdmin.viewings.cancelConfirm')}
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    className="rh-v4-btn rh-v4-btn--plain"
                    onClick={() => setConfirmCancel(b.id)}
                    data-testid={`property-viewings-cancel-${b.id}`}
                  >
                    {t('webAdmin.viewings.cancel')}
                  </button>
                )
              }
            />
          ))
        )}
      </V4Group>
      {confirmCancel !== null ? <p className="rh-a2-card__hint">{t('webAdmin.viewings.cancelHint')}</p> : null}
    </div>
  );
}
